<template lang="pug">
.column-chart
  .column-chart__header
    .column-chart__label {{ legend?.title }}
    .column-chart__buttons(v-if="canScrollLeft || canScrollRight", :style="{ marginRight: `${buttonsMarginRight}px` }")
      .column-chart__buttons-item(:class="scrollLeftClass", @click="scrollChart('left')") 
        q-icon(name="arrow_back_ios")
      .column-chart__buttons-item(:class="scrollRightClass", @click="scrollChart('right')")
        q-icon(name="arrow_forward_ios")

  apexchart(ref="expensesDynamic", type="candlestick", :height="height", :options="chartOptions", :series="series")
</template>

<script setup>
import { ref, onMounted, computed } from "vue";

const props = defineProps({
  /**
   * Дата самого чарта. Имеет несколько обязательных ключей: total, data и legend
   * Пример: {
   *  total: {
   *    label: "Всего",
   *    data: 5,
   *  },
   *  data: [
   *    {
   *      x: "2025-05-01T00:00:00.000Z",
   *      y: 2
   *    },
   *    {
   *      x: "2025-06-01T00:00:00.000Z",
   *      y: 3,
   *    }
   *  ],
   *  legend: {
   *    title: "Название графика"
   *  }
   * }
   */
  chartData: { type: Object, required: true, default: () => {} },
  /* Высота бара. Пример: 300 */
  height: { type: Number, default: () => 300 },
  /* Максимальное количество отображенных столбцов. Остальные можно посмотреть через скролл вправо-влево стрелками. Пример: 13 */
  maxSeries: { type: Number, default: () => 13 },
  /* Отступ кнопок скролла от правого края. Нужно для умещения кнопок с дашборда с позицией absolute. */
  buttonsMarginRight: { type: Number, default: () => 0 },
});
const series = ref([]); // Серии баров отображаемых пользователю внутри его скролла
const fullSeries = ref([]); // Все серии переданные от родителя
const legend = ref([]); // Данные легенды переданные от родителя
const canScrollLeft = ref(true); // Флаг возможности скроллить чарт влево
const canScrollRight = ref(true); // Флаг возможности скроллить чарт вправо
// Текущая позиция скролла. Хранит в себе первый и последний индексы массивов в fullSeries
const currentScrollPosition = ref([]);

// Опции самого чарта
const chartOptions = ref({
  chart: {
    type: "candlestick", // Тип чарта
    zoom: {
      enabled: false, // Запрещаем возможность зума
    },
    toolbar: {
      show: false, // Запрещаем дефолтный тулбар
    },
    events: {
      mouseMove: (e, chartContext, config) => {
        // Добавляем функцию на событие mousemove
        const xAxis = chartContext.el.querySelector(".apexcharts-xaxis-texts-g"); // DOM элемент оси X
        // DOM элемент полоски на оси X. Используются для эффекта зеленого гаснущего луча от бара к низу графика
        const crosshairs = chartContext.el.querySelector(".apexcharts-xcrosshairs");
        const tooltip = chartContext.el.querySelector(".apexcharts-tooltip"); // DOM элемент тултипа
        const tooltipWidth = tooltip?.getBoundingClientRect().width; // Ширина тултипа
        const barIndex = config.dataPointIndex; // Индекс массива текущего бара
        // Функция сброса закрашивания текста на оси X выделенного бара
        const resetXAxis = () => {
          xAxis.childNodes.forEach(item => {
            item.style.fill = "var(--header-subtitle-color)";
          });
        };
        if (tooltip) {
          // Если в DOM есть тултип задаем ему style visibility visible
          tooltip.style.visibility = "visible";
          // Если курсор за пределами любого из баров скрываем тултип и сбрасываем закрашивание оси X
          if (config.dataPointIndex === -1 || config.dataPointIndex === series.value[0].data.length) {
            tooltip.style.visibility = "hidden";
            resetXAxis();
            return;
          }
        }

        // DOM элемент текущего бара
        const currentBar = chartContext.el.querySelector(".apexcharts-series").childNodes[barIndex];
        const barWidth = Number(currentBar.getAttribute("barWidth")); // Ширина текущего бара
        const barHeight = Number(currentBar.getBoundingClientRect().height); // Высота текущего бара
        const offsetY = Number(currentBar.getAttribute("cy")); // Отступ от верха графика у бара
        // Отступ от левой части экрана графика
        const chartOffsetX = chartContext.el.querySelector(".apexcharts-graphical").getBoundingClientRect().left;
        // Отступ от левой части экрана до самих баров внутри графика
        const chartWrapperMargin =
          chartOffsetX - chartContext.el.querySelector(".apexcharts-canvas").getBoundingClientRect().left;
        // Разница между отступом от левой части экрана графика и левой части экрана самих баров
        const barMarginLeft =
          currentBar.getBoundingClientRect().left -
          chartContext.el.querySelector(".apexcharts-graphical").getBoundingClientRect().left;
        // Позиция у полосок посреди оси X на оси Y
        crosshairs.setAttribute("y", offsetY + barHeight);

        // Если высота полоски > 0 от низа графиков, то задаем ей высоту от низа до нижний границы бара
        if (crosshairs.getAttribute("y2") - offsetY - barHeight > 0) {
          crosshairs.setAttribute("height", crosshairs.getAttribute("y2") - offsetY - barHeight);
        } else {
          // Иначе назначаем ее 0
          crosshairs.setAttribute("height", "0");
        }

        // Назначаем длину этой полоски на всю ширину бара
        crosshairs.setAttribute("width", barWidth);
        // Смещаем на половину длины бара, т.к. полоска идет от центра - 2px (размер самой полоски 4px)
        crosshairs.style.transform = `translateX(-${barWidth / 2 - 2}px)`;
        // Аналогично для тултипа
        tooltip.style.marginTop = `${currentBar.getAttribute("cy")}px`;
        tooltip.style.marginLeft = `${chartWrapperMargin + barMarginLeft + barWidth / 2}px`;

        // Если тултип не умещается, зеркалим его в другую сторону через translate
        if (window.innerWidth - currentBar.getBoundingClientRect().left < tooltipWidth) {
          tooltip.style.transform = `translate(calc(-100% + ${barWidth / 2}px), -80%)`;
          tooltip.style.setProperty("--arrow-margin", `${tooltipWidth - barWidth / 2}px`);
        } else {
          tooltip.style.setProperty("--arrow-margin", `50%`);
          tooltip.style.transform = `translate(-50%, -80%)`;
        }

        // Сбрасываем подсветку текста на оси X
        resetXAxis();

        if (e.x > chartOffsetX) {
          // Если курсор не выходит за пределы графика подсвечиваем текущий элемент
          xAxis.childNodes[barIndex].style.fill = "#0EC262";
          tooltip.style.visibility = "visible";
        } else {
          // Иначе скрываем тултип
          tooltip.style.visibility = "hidden";
        }
      },
      mouseLeave: (e, chartContext) => {
        // При покидании курсором графика сбрасываем закрашивание оси X
        const xAxis = chartContext.el.querySelector(".apexcharts-xaxis-texts-g");
        xAxis.childNodes.forEach(item => {
          item.style.fill = "var(--header-subtitle-color)";
        });
      },
    },
  },
  // Цвет баров всегда статичный
  colors: ["#7C95FF"],
  // Убираем все дефолтные подсветки при hover и active
  states: {
    hover: {
      filter: { type: "none" },
    },
    active: {
      filter: { type: "none" },
    },
  },
  yaxis: {
    tooltip: {
      enabled: false, // Запрещаем отображение тултипа
    },
    min: 0,
    tickAmount: 4, // Кол-во секий на оси Y
  },
  xaxis: {
    type: "category",
    tickPlacement: "between", // Расположение тиков на которых будет crosshairs из которых сделаны зеленые полосы вниз
    tooltip: {
      enabled: false, // Скрываем тултип оси X
    },
    crosshairs: {
      show: true,
      position: "front", // Показываем перед тиками на оси X
      width: 2, // Ширина 2, которую мы вычитали выше
      // Заполнение тиков зеленым цветом с градиентом
      fill: {
        color: "#0EC262",
        type: "gradient",
        gradient: {
          colorFrom: "#0EC262",
          colorTo: "#0EC262",
          opacityFrom: 0.1,
          opacityTo: 0.7,
        },
      },
      stroke: {
        width: 0,
      },
    },
    axisBorder: {
      show: false, // Не показываем границы между категориями
    },
    axisTicks: {
      show: false, // Не показываем сами тики
    },
  },
  legend: {
    show: false, // Скрываем легенду
  },
  plotOptions: {
    // Цвета для верхней части candlestick (сами бары) и нижней (свеча из которой сделана зеленая полоса)
    candlestick: {
      colors: {
        upward: "#859DFF",
        downward: "#71C79F",
      },
    },
  },
  dataLabels: {
    enabled: true,
    offsetY: -12,
    formatter: function (val, opts) {
      /**
       * Дата для бара выглядит условно так [
       *  0,
       *  0,
       *  752932,
       *  752932,
       * ]
       * Где первые 0 и 1 индексы массива это начальная точка бара на графике, а 2 и 3 это конечная. Нам нужна разница.
       * Данные массива не задублированны, в 1 и 2 индексе по идее приходит "фитиль" бара. У нас он находится внутри бара чтобы его скрыть
       * По идее мы можем и сократить до двух элементов массива, т.к. мы больше не используем "фитиль" в качестве полоски до низа графика
       */
      const open = opts.w.config.series[0].data[parseInt(opts.dataPointIndex)].y[0];
      const close = opts.w.config.series[0].data[parseInt(opts.dataPointIndex)].y[3];
      return close - open;
    },
  },
  tooltip: {
    followCursor: false, // Запрещаем тултипу следовать за курсором
    // Задаем ему фиксированную позицию слева-сверху. Двигаем тултип в mousemove
    fixed: {
      enabled: true,
      position: "topLeft",
    },
    shared: false,
    custom: function (opts) {
      // Текст с оси X
      const date = opts.w.config.series[0].data[parseInt(opts.dataPointIndex)].x;
      // Нижнее значение тултипа
      const open = opts.w.config.series[0].data[parseInt(opts.dataPointIndex)].y[0];
      // Верхнее значение тултипа
      const close = opts.w.config.series[0].data[parseInt(opts.dataPointIndex)].y[3];

      // Текст тултипа
      let text =
        "<div class='column-chart__tooltip'><div class='column-chart__tooltip__header'><span class='column-chart__tooltip__circle'></span>";
      text += `<span class='column-chart__tooltip__label'>${close - open} ₽</span></div>`;
      text += `<span class='column-chart__tooltip__value'>${date}</span></div>`;

      return text;
    },
  },
});

const maxSeriesLength = computed(() => props.maxSeries);
// При максимальном значении maxSeriesLength равным 13 шаг скролла должен быть 4
// Шаг скролла кол-во баров на которое сдвигается чарт при скролле графика
// 4 это удобное число для скролла графика, чтоб пользователь скроллил график на треть, видя часть данных которые уже были чтоб не теряться
// Если максимальное число баров меньше, шаг скролла будет соответственно уменьшаться по формуле ниже
// Вычисление для других случаев когда максимальная длина меньше, как в дашборде AI
const seriesScrollStep = computed(() => Math.ceil(maxSeriesLength.value / 4));

const scrollLeftClass = computed(() => (canScrollLeft.value ? "active" : ""));
const scrollRightClass = computed(() => (canScrollRight.value ? "active" : ""));

const setFullSeries = () => {
  const newArr = [];
  let lastStart = 0;

  props.chartData?.data.forEach(item => {
    const currentEnd = Math.ceil(item.y + lastStart);
    newArr.push({
      x: item.x,
      y: [lastStart, lastStart, currentEnd, currentEnd],
    });
    lastStart = currentEnd;
  });

  newArr.push({
    x: props.chartData?.total.label,
    y: [0, 0, Math.ceil(props.chartData?.total.data), Math.ceil(props.chartData?.total.data)],
  });

  fullSeries.value = newArr;
};

const setChartParams = () => {
  legend.value = props.chartData?.legend;
  chartOptions.value.yaxis.max = props.chartData?.total.data;
  setFullSeries();

  if (fullSeries.value.length <= maxSeriesLength.value) {
    const positionStart = 0;
    const positionEnd = fullSeries.value.length;
    setNewSeries(positionStart, positionEnd);
  } else {
    const positionStart = fullSeries.value.length - maxSeriesLength.value;
    const positionEnd = fullSeries.value.length;
    setNewSeries(positionStart, positionEnd);
  }
};

const setNewSeries = (start, end) => {
  if (start < 0) {
    end = end - start;
    start = 0;
  }
  if (end > fullSeries.value.length) {
    start = start - (end - fullSeries.value.length);
    end = fullSeries.value.length;
  }
  currentScrollPosition.value = [start, end];
  series.value = [
    {
      name: "dynamics",
      data: fullSeries.value.slice(start, end),
    },
  ];
  start === 0 ? (canScrollLeft.value = false) : (canScrollLeft.value = true);
  end === fullSeries.value.length ? (canScrollRight.value = false) : (canScrollRight.value = true);
};

const scrollChart = direction => {
  if (canScrollRight.value || canScrollLeft.value) {
    if (direction === "right" && canScrollRight.value) {
      currentScrollPosition.value = currentScrollPosition.value.map(item => {
        return item + seriesScrollStep.value;
      });
    }
    if (direction === "left" && canScrollLeft.value) {
      currentScrollPosition.value = currentScrollPosition.value.map(item => {
        return item - seriesScrollStep.value;
      });
    }
    setNewSeries(currentScrollPosition.value[0], currentScrollPosition.value[1]);
  }
};

onMounted(() => {
  setChartParams();
});
</script>

<style lang="scss">
.column-chart {
  width: 100%;

  background-color: var(--expenses-content-background-color);

  border-radius: 20px;

  &__header {
    padding-top: 20px;
    margin-left: 20px;
    margin-right: 20px;

    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;

    .multiselect {
      width: 100px;
    }
  }

  &__legend {
    display: flex;
    flex-direction: column;

    &__item {
      display: flex;
      align-items: center;
      gap: 5px;

      &__circle {
        width: 8px;
        height: 8px;

        border-radius: 50%;

        background-color: #5b7aff;
      }
      &__label {
        font-weight: 40;
        font-size: 14px;
        color: #8a9099;
      }
    }
  }

  &__label {
    color: var(--expenses-main-text-color);
    font-size: 20px;
  }
  & .apexcharts-datalabel {
    font-weight: 400;
    fill: #5b7aff !important;
  }

  & .apexcharts-data-labels {
    rect {
      display: none;
    }
  }
  & .apexcharts-series path {
    clip-path: inset(0 0 0 0 round 8px);
  }
  & .apexcharts-xaxis-label,
  & .apexcharts-yaxis-label {
    fill: var(--header-subtitle-color);
  }

  .apexcharts-xcrosshairs {
    position: relative;
  }

  .column-chart__buttons {
    display: flex;
    width: 40px;
    justify-content: space-between;
    user-select: none;

    &-item {
      opacity: 0.5;
      cursor: default;
      color: var(--expenses-main-text-color);

      &.active {
        opacity: 1;
        cursor: pointer;
      }
    }
  }

  .apexcharts-tooltip {
    border-radius: 12px !important;
    box-shadow: var(--expenses-charts-tooltip-box-shadow) !important;
    border: none !important;
    --arrow-margin: 50%;
    overflow: visible;
    background-color: var(--expenses-content-background-color) !important;

    &::after {
      content: "";
      position: absolute;
      border: 11px solid transparent;
      border-top: 11px solid var(--expenses-content-background-color);
      bottom: -22px;
      left: var(--arrow-margin);
      transform: translateX(-50%);
    }
    .column-chart__tooltip {
      padding: 8px 20px;
      background-color: var(--expenses-content-background-color);
      width: 188px;
      height: 60px;
      text-align: center;
      border-radius: 12px !important;

      &__header {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 5px;
      }

      &__circle {
        width: 8px;
        height: 8px;

        border-radius: 50%;

        background-color: #5b7aff;
      }

      &__label {
        font-weight: 400;
        font-size: 14px;
        color: var(--expenses-main-text-color);
      }
      &__value {
        font-weight: 400;
        font-size: 14px;
        color: var(--expenses-charts-tooltips-subtext-color);
      }
    }
  }
}
</style>
